import iccConfig from './pathologies/icc';
import htaConfig from './pathologies/hta';
import psmConfig from './pathologies/psm';
import epocConfig from './pathologies/epoc';
import pediatricConfig from './pathologies/pediatric';
import mgaConfig from './pathologies/mga';
import mgcConfig from './pathologies/mgc';
import { COLUMN_SETS } from './healthDashboardColumns';

/**
 * Centralized Pathology Configuration Registry
 *
 * This configuration object serves as the single source of truth for all
 * pathology-specific behavior, features, and metadata across the application.
 *
 * Each pathology is now defined in its own module file under ./pathologies/
 * This provides better modularity and easier maintenance.
 *
 * Usage:
 * - Use getPathologyConfig(pathologyId, modality) to retrieve configuration
 * - Use the usePathologyConfig hook in components for reactive behavior
 *
 * Internationalization:
 * - displayNameKey contains the i18n translation key (e.g., 'icc', 'hta')
 * - Use with useTranslation: const { t } = useTranslation(); t(displayNameKey)
 * - Translation keys are defined in src/locales/*.json files
 */

export const PATHOLOGY_CONFIGS = {
  [iccConfig.id]: iccConfig,
  [htaConfig.id]: htaConfig,
  [psmConfig.id]: psmConfig,
  [epocConfig.id]: epocConfig,
  [pediatricConfig.id]: pediatricConfig,
  [mgaConfig.id]: mgaConfig,
  [mgcConfig.id]: mgcConfig,
};

/**
 * Retrieves the configuration for a specific pathology
 *
 * @param {number} pathologyId - The pathology ID (use PATHOLOGY_IDS constants)
 * @param {number|null} modality - Optional modality ID (for pathologies like HTA)
 * @returns {Object|null} Configuration object or null if not found
 */
export const getPathologyConfig = (pathologyId, modality = null) => {
  // Return null silently if pathologyId is not provided (initial state)
  if (pathologyId === null || pathologyId === undefined) {
    return null;
  }

  const config = PATHOLOGY_CONFIGS[pathologyId];

  if (!config) {
    console.warn(`Pathology configuration not found for ID: ${pathologyId}`);
    return null;
  }

  // If modality is requested and exists, merge modality-specific config
  if (modality && config.modalities && config.modalities[modality]) {
    const modalityConfig = config.modalities[modality];
    return {
      ...config,
      dashboard: {
        ...config.dashboard,
        ...modalityConfig,
      },
      currentModality: modality,
    };
  }

  return config;
};

/**
 * Checks if a pathology has a specific feature enabled
 *
 * @param {number} pathologyId - The pathology ID
 * @param {string} featureName - The feature to check (e.g., 'surveys', 'ecg')
 * @returns {boolean} True if feature is enabled
 */
export const hasPathologyFeature = (pathologyId, featureName) => {
  const config = PATHOLOGY_CONFIGS[pathologyId];
  return config?.features?.includes(featureName) || false;
};

/**
 * Gets all pathologies that have a specific feature enabled
 *
 * @param {string} featureName - The feature to check
 * @returns {number[]} Array of pathology IDs that have the feature
 */
export const getPathologiesWithFeature = (featureName) => {
  return Object.values(PATHOLOGY_CONFIGS)
    .filter((config) => config.features.includes(featureName))
    .map((config) => config.id);
};

/**
 * Gets the export transformer function for a pathology
 *
 * @param {number} pathologyId - The pathology ID
 * @param {number|null} modality - Optional modality ID
 * @returns {Function|null} Export transformer function
 */
export const getExportTransformer = (pathologyId, modality = null) => {
  const config = getPathologyConfig(pathologyId, modality);
  return config?.dashboard?.exportTransformer || null;
};

/**
 * Gets the health dashboard column configuration for a pathology
 *
 * @param {number} pathologyId - The pathology ID
 * @param {number|null} modality - Optional modality ID (for pathologies like HTA)
 * @param {Function} navigate - React Router navigate function
 * @param {Function} t - i18n translation function
 * @param {Function} openChat - Opens the shared chat modal for a given patient id
 * @returns {Array} Column configuration array for MaterialTable
 */
export const getHealthDashboardColumns = (pathologyId, modality, navigate, t, openChat) => {
  const config = getPathologyConfig(pathologyId, modality);

  if (!config?.dashboard?.columnSet) {
    console.warn(`No column set defined for pathology ID: ${pathologyId}`);
    return [];
  }

  const columnSetName = config.dashboard.columnSet;
  const columnFactory = COLUMN_SETS[columnSetName];

  if (!columnFactory) {
    console.warn(`Column set '${columnSetName}' not found in COLUMN_SETS registry`);
    return [];
  }

  return columnFactory(navigate, t, openChat);
};

export default {
  PATHOLOGY_CONFIGS,
  getPathologyConfig,
  hasPathologyFeature,
  getPathologiesWithFeature,
  getExportTransformer,
  getHealthDashboardColumns,
};
